import { useEffect, useState, useRef, Fragment, ChangeEvent } from 'react';
import styles from './styles/Scoreboard.module.scss';
import { useGamesContainer } from 'packs/pages/widgets/v5/Show';
import useScreenSize from 'hooks/useScreenSize';
// @ts-ignore
import { SBLMobileDate } from 'react/admin/components/admin_components';
import HorizontalScoreboardGame from './ScoreboardGame';
import cn from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import GENDER_SPORTS, { findGenderSport } from 'constants/GenderSports';
import HorizontalWidgetDatepicker from './Datepicker';
import Selector from 'components/Selector';
import Sponsor, { HORIZONTAL_WIDGET } from 'components/Sponsor';
import { makeUrl } from 'helpers/url';

const goToPage = (selectedSport: { genderId?: number; sportId?: number } = {}, selectedDate: Dayjs) => {
  window.location.href = makeUrl(window.location.href, {
    gender_id: selectedSport.genderId,
    sport_id: selectedSport.sportId,
    date: selectedDate.format('YYYY-MM-DD')
  });
};

interface GamesHorizontalScoreboardProps {
  showSBLAd: boolean;
  widgetId?: number;
}

//showing specific ad for WIAA widget
const WIAA_ID = 94;

export default function Scoreboard({ showSBLAd, widgetId }: GamesHorizontalScoreboardProps) {
  const [{ games, genderSports, variables }, { loadNextPage }] = useGamesContainer();
  const scoreboardElement = useRef<HTMLDivElement>(null);
  const { sport_id, gender_id, date } = variables;
  const genderSportOptions = genderSports.length > 1 ? genderSports : GENDER_SPORTS;
  const { width } = useScreenSize();
  const selectedSport = sport_id && gender_id ? findGenderSport(sport_id, gender_id) : undefined;
  const selectedDate = dayjs(date);
  const useDesktopDate = width && width < 600;

  const [showDate, setShowDate] = useState(false);
  const [hasLeftGames, setHasLeftGames] = useState(false);
  const [hasRightGames, setHasRightGames] = useState(games && games.length > 3);

  useEffect(() => setShowDate(false), [games]);

  const adsInterval = 4; //isSmallScreen ? 4 : 8;
  const gameScoreWidth = 220;

  const handleScroll = (isRight: boolean) => {
    if (!scoreboardElement.current) return;

    const maxScroll = scoreboardElement.current.scrollWidth - scoreboardElement.current.offsetWidth;
    let nextScroll = scoreboardElement.current.scrollLeft;
    nextScroll = isRight ? nextScroll + 2 * gameScoreWidth : nextScroll - 2 * gameScoreWidth;

    if (nextScroll <= 0) nextScroll = 0;
    setHasLeftGames(nextScroll !== 0);

    if (nextScroll >= maxScroll) nextScroll = maxScroll;
    setHasRightGames(nextScroll !== maxScroll);

    scoreboardElement.current.scrollLeft = nextScroll;
  };

  const handleOnBlur = () => {
    if (!selectedDate.isSame(date, 'day')) {
      goToPage(selectedSport, selectedDate);
    }
  };

  const handleDateChange = (date: Dayjs) => {
    goToPage(selectedSport, date);
  };

  const handleSportChange = (genderSport?: (typeof genderSports)[number]) => {
    goToPage(genderSport, selectedDate);
  };

  const handleHorizontalScroll = () => {
    if (!scoreboardElement.current) return;
    const threshold = gameScoreWidth + scoreboardElement.current.scrollLeft + scoreboardElement.current.offsetWidth;
    if (threshold > scoreboardElement.current.scrollWidth) loadNextPage();
  };

  const showAd = (index: number) => {
    if (!showSBLAd) return false;
    if ((index + 1) % adsInterval === 0) return true;
    return games.length < adsInterval && games.length - 1 === index;
  };

  return (
    <div className={styles.SBLWidgetV5}>
      <div className={styles.SBLWidgetV5Selectors}>
        {useDesktopDate ? (
          <SBLMobileDate
            label="date"
            format="MMM. D"
            className={styles.hideDesktop}
            onBlur={handleOnBlur}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleDateChange(dayjs(e.target.value))}
            value={selectedDate}
          />
        ) : (
          <div className={styles.DesktopDate}>
            <div className={styles.SBLLabel}>Date</div>
            <div onClick={() => setShowDate(!showDate)}>{selectedDate.format('MMM. D')}</div>
          </div>
        )}
        <Selector
          label="Sport"
          className={styles.SportSelector}
          placeholder="All Sports"
          value={selectedSport}
          onChange={handleSportChange}
          options={genderSportOptions}
          labelExtractor={o => (useDesktopDate ? o.name.replace('Boys', 'B.').replace('Girls', 'G.') : o.name)}
          valueExtractor={o => `${o.sportId}-${o.genderId}`}
        />
      </div>
      <div className={styles.SBLWidgetGameContainer}>
        {showDate && !useDesktopDate && <HorizontalWidgetDatepicker value={selectedDate} handleChange={handleDateChange} />}
        <div
          onClick={() => handleScroll(false)}
          className={cn(styles.SBLWidgetArrow, styles.hideMobile, { [styles.DimmedArrow]: !hasLeftGames })}
        />
        <div ref={scoreboardElement} className="sbl-game-list" onScroll={handleHorizontalScroll}>
          {games.length === 0 && <div className={styles.NoGamesDiv}>No games today</div>}
          {games.map((game, index) => (
            <Fragment key={game.id}>
              <HorizontalScoreboardGame game={game} showGenderSport={!sport_id || !gender_id} />
              {showAd(index) && <Sponsor placement={HORIZONTAL_WIDGET} className={styles.SBLWidgetAd} isWIAA={widgetId === WIAA_ID} />}
            </Fragment>
          ))}
        </div>
        <div
          onClick={() => handleScroll(true)}
          className={cn(styles.SBLWidgetArrow, styles.SBLWidgetRightArrow, styles.hideMobile, { [styles.DimmedArrow]: !hasRightGames })}
        />
      </div>
    </div>
  );
}
